import { DatePipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import Dutch from '@angular/common/locales/nl';
import { ErrorHandler, LOCALE_ID, NgModule, Provider, Type } from '@angular/core';
import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { JWT_OPTIONS, JwtModule } from '@auth0/angular-jwt';
import { JwtConfig } from '@auth0/angular-jwt/lib/angular-jwt.module';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';
import { ToastrModule } from 'ngx-toastr';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ComponentsModule } from './components/components.module';
import { AccordionController } from './controllers/accordion.controller';
import { BaseUrlInterceptor } from './interceptors/base-url.interceptor';
import { DateHttpInterceptorService } from './interceptors/date-http-interceptor.service';
import { ErrorHandlerService } from './services/app-insights/error-handler.service';
import { AuthenticationProviderServiceImpl } from './services/authentication-provider-service-impl.service';
import { AuthenticationService } from './services/authentication.service';
import { PaulaDatabaseServiceMock } from './services/mock/paula-database.service.mock';
import { ProjectFormServiceMock } from './services/mock/project-form.service.mock';
import { ProjectJobAttachmentServiceMock } from './services/mock/project-job-attachment.service.mock';
import { ProjectJobServiceMock } from './services/mock/project-job.service.mock';
import { ProjectObjectServiceMock } from './services/mock/project-object.service.mock';
import { ProjectServiceMock } from './services/mock/project.service.mock';
import { ProjectJobAnswerServiceImpl } from './services/project-job-answer.service';
import { TRANSLATION_EN, TRANSLATION_NL } from './translations';
import { environment } from '../environments/environment';
import { SortablejsModule } from './library/ngx-sortablejs/sortablejs.module';
import { LanguageService } from './services/language.service';
import { AuthenticationProviderServiceMock } from './services/mock/authentication-provider.service.mock';
import { ObstructionCodeServiceMock } from './services/mock/obstruction-code.service.mock';
import { ProjectJobAnswerMetaServiceMock } from './services/mock/project-job-answer-meta.service.mock';
import { ProjectJobAnswerServiceMock } from './services/mock/project-job-answer.service.mock';
import { ObstructionCodeServiceImpl } from './services/obstruction-code.service';
import { PaulaDatabaseServiceImpl } from './services/paula-database.service';
import { ProjectFormServiceImpl } from './services/project-form.service';
import { ProjectJobAnswerMetaServiceImpl } from './services/project-job-answer-meta.service';
import { ProjectJobAttachmentServiceImpl } from './services/project-job-attachment.service';
import { ProjectJobServiceImpl } from './services/project-job.service';
import { ProjectObjectServiceImpl } from './services/project-object.service';
import { ProjectService } from './services/project.service';

registerLocaleData(Dutch);

export function jwtOptionsFactory(authenticationService: AuthenticationService): JwtConfig {
    const allowedDomains: (string | RegExp)[] = [/^.*\.check.secuur.io$/, 'localhost:8080'];
    if (environment.apiHost) {
        // Add custom apiHost from environment to allowedDomains
        const apiUrl = new URL(environment.apiHost);
        const protocol = apiUrl.protocol;
        const nonStandardPort = ('https:' === protocol && apiUrl.port !== '443') || ('http:' === protocol && apiUrl.port !== '80');
        allowedDomains.push(`${apiUrl.hostname}${nonStandardPort && apiUrl.port ? ':' + apiUrl.port : ''}`);
    }

    return {
        tokenGetter: () => {
            return authenticationService.getJwtToken();
        },
        allowedDomains: allowedDomains,
        disallowedRoutes: [/^\/oauth2.*$/g]
    };
}

export function serviceWithMock(provide: string, service: Type<unknown>, mock: Type<unknown>, extra: object = {}): Provider {
    return {
        useClass: environment.useMock ? mock : service,
        provide: provide,
        ...extra
    };
}

@NgModule({
    declarations: [
        AppComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserAnimationsModule,
        SortablejsModule.forRoot({ animation: 150 }),
        BrowserModule,
        HammerModule,
        TranslateModule.forRoot(),
        ToastrModule.forRoot({
            timeOut: 4000,
        }),
        JwtModule.forRoot({
            config: {
                throwNoTokenError: true,
                skipWhenExpired: false
            },
            jwtOptionsProvider: {
                provide: JWT_OPTIONS,
                useFactory: jwtOptionsFactory,
                deps: [AuthenticationService],
            },
        }),
        NgSelectModule,
        AppRoutingModule,
        ComponentsModule], providers: [
        { provide: LOCALE_ID, useValue: 'nl' },
        { provide: ErrorHandler, useClass: ErrorHandlerService },
        serviceWithMock('ProjectService', ProjectService, ProjectServiceMock),
        serviceWithMock('ProjectJobService', ProjectJobServiceImpl, ProjectJobServiceMock),
        serviceWithMock('ProjectJobAnswerService', ProjectJobAnswerServiceImpl, ProjectJobAnswerServiceMock),
        serviceWithMock('PaulaDatabaseService', PaulaDatabaseServiceImpl, PaulaDatabaseServiceMock),
        serviceWithMock('ProjectFormService', ProjectFormServiceImpl, ProjectFormServiceMock),
        serviceWithMock('ProjectObjectService', ProjectObjectServiceImpl, ProjectObjectServiceMock),
        serviceWithMock('ProjectJobAnswerMetaService', ProjectJobAnswerMetaServiceImpl, ProjectJobAnswerMetaServiceMock),
        serviceWithMock('ObstructionCodeService', ObstructionCodeServiceImpl, ObstructionCodeServiceMock),
        serviceWithMock('AuthenticationProviderService', AuthenticationProviderServiceImpl, AuthenticationProviderServiceMock),
        serviceWithMock('ProjectJobAttachmentService', ProjectJobAttachmentServiceImpl, ProjectJobAttachmentServiceMock),
        LanguageService,
        AccordionController,
        DatePipe,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: BaseUrlInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: DateHttpInterceptorService,
            multi: true
        },
        provideHttpClient(withInterceptorsFromDi()),
    ]
})
export class AppModule {
    constructor(
        private languageService: LanguageService,
    ) {
        languageService.init({
            nl: TRANSLATION_NL,
            en: TRANSLATION_EN,
        });
    }
}
